import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import api from '../../utils/api';

const LlmIntegration = () => {
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [activeProvider, setActiveProvider] = useState('');
  const [routingMode, setRoutingMode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch current settings
        try {
          const response = await api.get('/api/admin/llm-settings/');
          const settings = response.data || response;
          if (settings && settings.model_name) {
            setSelectedModel(settings.model_name);
          }
        } catch (settingsErr) {
          console.warn('Failed to fetch LLM settings:', settingsErr);
        }

        // Fetch available models from the correct admin endpoint
        try {
          const response = await api.get('/api/admin/llm-models/');
          const modelData = response.data || response;

          if (modelData && modelData.models && Array.isArray(modelData.models) && modelData.models.length > 0) {
            // Handle both object format and string format
            const models = modelData.models.map(model => {
              if (typeof model === 'string') {
                return { name: model, provider: 'Ollama', tier: 'default' };
              }
              return model;
            });
            setAvailableModels(models);

            // Set default selection to first model if not already set
            if (!selectedModel && models.length > 0) {
              setSelectedModel(models[0].name);
            }
          } else {
            // Fallback models
            setAvailableModels([
              { name: 'llama3', provider: 'Ollama', tier: 'default' },
              { name: 'llama2', provider: 'Ollama', tier: 'default' },
              { name: 'mistral', provider: 'Ollama', tier: 'default' }
            ]);
          }

          // Set provider info if available
          if (modelData.active_provider) {
            setActiveProvider(modelData.active_provider);
          }
          if (modelData.routing_mode) {
            setRoutingMode(modelData.routing_mode);
          }
        } catch (modelErr) {
          console.warn('Failed to fetch available models:', modelErr);
          setAvailableModels([
            { name: 'llama3', provider: 'Ollama', tier: 'default' },
            { name: 'llama2', provider: 'Ollama', tier: 'default' }
          ]);
        }

        setError(null);
      } catch (err) {
        console.error('Failed to initialize LLM integration:', err);
        setError('Failed to load LLM settings. Using default models.');
        setAvailableModels([
          { name: 'llama3', provider: 'Ollama', tier: 'default' }
        ]);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      await api.put('/api/admin/llm-settings/', { model_name: selectedModel });
      setSuccess(true);
    } catch (err) {
      console.error('Failed to save LLM settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get display label for model
  const getModelLabel = (model) => {
    if (typeof model === 'string') return model;
    const tierLabel = model.tier ? ` (${model.tier})` : '';
    return `${model.name} - ${model.provider}${tierLabel}`;
  };

  // Helper to get model value
  const getModelValue = (model) => {
    return typeof model === 'string' ? model : model.name;
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">LLM Integration Settings</h2>

      {/* Provider Status Section */}
      {activeProvider && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-md">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Current Configuration</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Active Provider:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">{activeProvider}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Routing Mode:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">{routingMode || 'single'}</span>
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Configure the LLM model for legal research and document analysis features.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md flex items-center">
          <CheckIcon className="h-5 w-5 mr-2" />
          Settings saved successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
        {/* Model Selection */}
        <div>
          <label htmlFor="llmModel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            LLM Model
          </label>
          <select
            id="llmModel"
            name="llmModel"
            value={selectedModel}
            onChange={handleModelChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          >
            {availableModels.map((model, index) => (
              <option key={`${getModelValue(model)}-${index}`} value={getModelValue(model)}>
                {getModelLabel(model)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Select which model to use for AI-powered features.
          </p>
        </div>

        {/* Available Models List */}
        {availableModels.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Available Models by Provider</h4>
            <div className="border rounded-md dark:border-gray-600 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Model</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Provider</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tier</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {availableModels.map((model, index) => (
                    <tr key={`table-${getModelValue(model)}-${index}`}>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{getModelValue(model)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{model.provider || 'Ollama'}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${model.tier === 'primary' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          model.tier === 'light' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            model.tier === 'safety' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              model.tier === 'reasoning' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                          {model.tier || 'default'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {loading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <CheckIcon className="h-5 w-5 mr-2" />
            )}
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LlmIntegration;

